import type { ReactNode } from 'react'

/**
 * What's New, as data rather than prose.
 *
 * These entries were 892 lines of hand-written JSX inside WhatsNewSection.tsx: a title and a
 * body per item, newest first, with no id, no date and no version anywhere in the file. Good
 * copy, written by whoever made each change, and completely inert - nothing could express
 * "newer than what you last saw", so nothing could put a new entry in front of anyone.
 *
 * Giving each entry an `id` is what makes the dialog possible. "Seen" is stored per user as the
 * id of the newest entry they have been shown, and anything above it in this array is unseen.
 *
 *
 * ADDING AN ENTRY
 *
 * Put it at the TOP, give it an id nothing else uses, and write the body as you would write it
 * for the person who has to use the thing. That is the whole process: adding an entry here is
 * what causes the dialog to appear, so an entry is a deliberate act of telling people something,
 * not a side effect of deploying.
 *
 * Deploys are NOT the trigger. `origin/prod` takes roughly eight commits a week and most of them
 * are bug fixes and internal work with nothing a user would notice. A dialog on every deploy
 * would interrupt people several times a week to say nothing, and they would learn to dismiss it
 * unread - including the once a release genuinely mattered.
 *
 *
 * IDS ARE PERMANENT
 *
 * Changing a `title` or a `body` is free. Changing an `id`, or reordering entries past one
 * another, re-shows that entry to everybody who had already seen it. Reword freely; renumber
 * never.
 *
 * Bodies stay JSX rather than markdown or plain strings because many of them carry `<a href="#...">`
 * links into other Help sections and `<span className="font-medium">` emphasis, all of which
 * would otherwise have to be re-authored.
 *
 * Dates are deliberately absent. The 70 entries below never had one, recovering them means
 * git-blaming 892 lines, and the feature needs ordering and identity, which the array gives for
 * free. `date` exists as an optional field for new entries that want to show one.
 */

export interface WhatsNewEntry {
  /** Stable and permanent. Changing it re-shows the entry to everyone. */
  id: string
  title: string
  /** JSX, because entries link into Help and carry emphasis. */
  body: ReactNode
  /** Optional, and not back-filled onto the historical entries. */
  date?: string
}

/** Newest first. The order is the chronology; there are no dates to sort by. */
export const WHATS_NEW: WhatsNewEntry[] = [
  {
    id: 'welcome-back-summary',
    title: 'A Summary When You Log In',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The first time you log in each day, a short summary now shows where you left off and what
          needs you next. <span className="font-medium">Where you left off</span> lists the records
          you changed most recently. Next to it is the work that matters for your role: tasks,
          meetings and deals closing for sales; your open tickets, SLA breaches first, for Support;
          orders waiting to be processed for the Orders team; and new leads assigned to you for
          Marketing. Anything waiting on you, such as a contract review or a discount approval,
          appears too. Click any item to go straight to it. It appears once a day, never when you
          simply reload the page.
        </p>
      </>
    ),
  },
  {
    id: 'a-guide-to-sales-trackers',
    title: 'A Guide to Sales Trackers',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Sales Trackers now has a proper guide in Help, covering the monthly QuickBooks upload, the
          Overview, targets, the weekly forecast, transactions and the Products &amp; reps page,
          along with the things that catch people out: why an empty target box is not a target of
          zero, why forecast and invoiced are never added together, and why retiring a product does
          not remove its history. Read it under{' '}
          <a
            href="#sales-trackers"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Sales Trackers
          </a>{' '}
          in Reports &amp; Analytics.
        </p>
      </>
    ),
  },
  {
    id: 'every-transaction-behind-the-numbers',
    title: 'Every Transaction Behind the Numbers',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          A new Transactions page lists the individual invoice lines behind whatever period you are
          looking at, so a total that looks wrong can be checked against what actually went into it.
          Filter by rep, by category, or by searching the customer and sales account. Two of the
          filters answer the question people ask most:{' '}
          <span className="font-medium">Unattributed</span> finds real sales not yet linked to
          anybody, which is the usual reason the reps do not add up to the company total, and{' '}
          <span className="font-medium">House accounts</span> finds the sales that belong to the
          company rather than to a person. The export gives you everything matching your filters,
          not just the page on screen. Reps have the same page for their own sales under{' '}
          <span className="font-medium">My transactions</span>.
        </p>
      </>
    ),
  },
  {
    id: 'month-and-quarter-on-sales-trackers',
    title: 'Month and Quarter on Sales Trackers',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Sales Trackers used to show a whole fiscal year and nothing else. Every page now has a
          month, quarter and year selector, and{' '}
          <span className="font-medium">opens on the current month</span>, which is what most people
          wanted to see first. Quarters follow the fiscal year rather than the calendar, so Q1 is
          March to May and Q4 is December to February. Your choice carries with you as you move
          between Overview, By Rep, By Product and Transactions.
        </p>
      </>
    ),
  },
  {
    id: 'targets-add-up-properly-again',
    title: 'Targets Add Up Properly Again',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Typing a Software target and then a Service target could leave the Overall figure stuck on
          a nonsense number, because it was filled in from the first digit you typed and then
          stopped following. Overall now keeps up with both parts as you type. Type your own Overall
          and it stays exactly as you entered it, even where that is deliberately more or less than
          the two parts added together, and clearing it hands the row back to the automatic total.
        </p>
      </>
    ),
  },
  {
    id: 'weekly-forecast-admins',
    title: 'Weekly Forecast (Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          A new tab on Sales Trackers for the Monday sales meeting: last week&rsquo;s forecast
          against this week&rsquo;s, and what has actually been invoiced, for each rep, against the
          month&rsquo;s target. The forecast is the CRM&rsquo;s weighted pipeline, so a deal that is
          not in the CRM will not appear here. Figures are captured automatically every Monday
          morning and cannot be worked out after the fact, so the comparison starts from the first
          capture and fills in from there. Forecast and invoiced are shown side by side and never
          added together, because a deal that closes turns into an invoice and counting both would
          count the same money twice. You can reopen an earlier week to see the figures as they
          stood that day.
        </p>
      </>
    ),
  },
  {
    id: 'all-targets-on-one-page-admins',
    title: 'All Targets on One Page (Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          A new Targets tab sets the yearly target for the company and for every rep in one place,
          instead of opening each rep&rsquo;s sheet in turn. Type an annual figure and it is divided
          across the twelve months for you. The bottom row adds up the rep targets and tells you how
          they compare with the company figure, which is the thing that was hard to check before.
          Where a target was set month by month, the row says so and warns you before an even split
          replaces it. An empty box still means no target rather than a target of zero, and
          month-by-month editing is still there behind <span className="font-medium">Months</span>{' '}
          on each row.
        </p>
      </>
    ),
  },
  {
    id: 'email-the-customer-from-an-order-orders-and-admins',
    title: 'Email the Customer From an Order (Orders & Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When you finalise an order you can now email the customer from the order itself. Pick one
          of the 26 templates, and the CRM fills in the customer, contact, licence term, users and
          product list, then inserts the right Account Manager paragraph based on who owns the
          account. Personalise the wording, attach files from your machine, and it sends from your
          own Gmail with a copy recorded against the order. Anything the CRM couldn&rsquo;t fill in
          is highlighted rather than left blank, so an unfinished template cannot go out looking
          finished.
        </p>
      </>
    ),
  },
  {
    id: 'sales-targets-on-sales-trackers-admins',
    title: 'Sales Targets on Sales Trackers (Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Overview now shows target against actual for the year, with the difference and the
          percentage above or below, for overall sales and for each of Software, Other services and
          Remediation. Targets are set month by month, and there is a{' '}
          <span className="font-medium">Spread</span> box if you would rather type one yearly figure
          and divide it across the twelve months. Each rep can have their own targets too, set from
          their sheet. An empty box means no target rather than a target of zero, so a year with
          only a few months filled in is measured on those months and not reported as a shortfall
          against the rest.
        </p>
      </>
    ),
  },
  {
    id: 'sales-trackers-one-rep-at-a-time',
    title: 'Sales Trackers: One Rep at a Time',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          By Rep no longer opens on a table of everybody. Choose a rep and you get their sheet:
          total sales, sales by product, the software and service split, their targets, and how they
          are tracking against them. Their individual billing lines are underneath, where you can
          still reattribute a line to somebody else.
        </p>
      </>
    ),
  },
  {
    id: 'sales-trackers-manage-products-and-reps-admins',
    title: 'Sales Trackers: Manage Products and Reps (Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The Products &amp; reps page (previously Attribution) now lists every sales account
          QuickBooks has sent, what each one counts as, and what it billed. New accounts appear
          automatically after an upload, and assigning a category now saves correctly, which it did
          not before. The same page lists who counts as a rep, so you can add somebody before their
          first sale in order to set their target.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">
            Making a product or rep inactive retires it, it does not remove it.
          </span>{' '}
          A retired product or rep drops out of the drop-downs and stops the CRM asking you to
          categorise it, but every sale it ever made keeps counting in the totals, the product
          breakdown and the rep breakdown. That is deliberate: if retiring something erased its
          history the product split would stop adding up to the company total. A discontinued
          product still shows in the months it actually sold, and drops off a year by itself once it
          sells nothing in that year.
        </p>
      </>
    ),
  },
  {
    id: 'sales-trackers-amounts-are-always-cad',
    title: 'Sales Trackers Amounts Are Always CAD',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The &ldquo;Amounts&rdquo; drop-down has gone. Every figure on Sales Trackers is CAD,
          converted at the rate stored on each upload, which is the basis that matches the Sales
          Tracker workbook. The alternative it used to offer added Australian dollars to Canadian
          dollars without converting them, so it was never a figure worth reading. The Australian
          entity&rsquo;s rate is still set on the upload page, where an AU month will read about 10%
          below the raw QuickBooks column once converted.
        </p>
      </>
    ),
  },
  {
    id: 'your-submitted-tickets-stay-visible',
    title: 'Your Submitted Tickets Stay Visible',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Tickets you created no longer disappear when Support takes ownership or resolves them —
          they stay in your ticket list, in the account&rsquo;s Tickets tab, and in your stat tiles.
          You can also now <span className="font-medium">add comments</span> to tickets you created
          or own (e.g. your Trial Requests), so there&rsquo;s always a place for follow-up notes.
          See{' '}
          <a
            href="#ticket-comments"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Comments
          </a>{' '}
          under Ticketing.
        </p>
      </>
    ),
  },
  {
    id: 'notes-when-creating-a-lead',
    title: 'Notes When Creating a Lead',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The New Lead form now includes a Description field, and the Description on the lead detail
          page saves properly on its own (it previously showed &ldquo;No fields to update&rdquo;).
          Your notes carry through to the contact, account, and opportunity when the lead converts.
        </p>
      </>
    ),
  },
  {
    id: 'delete-contacts-admins',
    title: 'Delete Contacts (Admins)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Admins can delete a contact from its detail page. A confirmation warning explains the
          consequences before anything happens; related records keep their history.
        </p>
      </>
    ),
  },
  {
    id: 'mass-upload-records-amounts-in-cad',
    title: 'Mass Upload Records Amounts in CAD',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Opportunity mass uploads now always record amounts in CAD, regardless of the
          account&rsquo;s currency — upload-file amounts are CAD by definition. Admins also get a{' '}
          <span className="font-medium">Verify currencies</span> button on the Mass Upload page that
          checks every past upload, fixes any record not in CAD, and produces a detailed
          downloadable report of exactly what changed.
        </p>
      </>
    ),
  },
  {
    id: 'clearer-error-messages',
    title: 'Clearer Error Messages',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When something fails, the CRM now explains what happened and what to do next in plain
          language — no more raw &ldquo;status code&rdquo; errors. Rate-limit messages include how
          long to wait, and stability fixes stop ticket pages from unexpectedly logging you out
          seconds after they load.
        </p>
      </>
    ),
  },
  {
    id: 'choose-what-to-sync-when-connecting-google',
    title: 'Choose What to Sync When Connecting Google',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Connecting your Google account is a one-time, per-user step in Settings — sync is never
          turned on automatically. When a Super Admin connects, new checkboxes let them opt out of
          Gmail and/or Calendar sync at connect time. See the new{' '}
          <a
            href="#google-integration"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Google Integration
          </a>{' '}
          guide under Administration.
        </p>
      </>
    ),
  },
  {
    id: 'opportunities-kanban-view',
    title: 'Opportunities Kanban View',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The Opportunities page now offers a Kanban board grouped by stage. Drag a deal card
          between columns to change its stage — a fast way to advance deals while working the
          pipeline. See{' '}
          <a
            href="#opportunities"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Opportunities
          </a>{' '}
          under CRM.
        </p>
      </>
    ),
  },
  {
    id: 'fiscal-year-this-year-and-year-to-date',
    title: 'Fiscal-Year “This Year” & Year-to-Date',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Dashboard and report date presets for &ldquo;This Year&rdquo; / Year-to-Date now follow
          Grackle's fiscal year (March 1 to the end of February) instead of the calendar year, so
          the numbers match how the business reports performance.
        </p>
      </>
    ),
  },
  {
    id: 'lead-source-column-on-leads',
    title: 'Lead Source Column on Leads',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The Leads list now includes a sortable Lead Source column, making it easy to group and
          prioritize leads by where they came from. Leads captured via web forms have their source
          filled in automatically. See{' '}
          <a
            href="#leads"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Leads
          </a>{' '}
          under CRM.
        </p>
      </>
    ),
  },
  {
    id: 'click-to-edit-on-detail-pages',
    title: 'Click-to-Edit on Detail Pages',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Edit fields inline by clicking the value directly on the detail page — no separate edit
          form needed. Available on Contact, Quote, and RFP detail pages, in addition to Tickets.
        </p>
      </>
    ),
  },
  {
    id: 'internal-support-request-form',
    title: 'Internal Support Request Form',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Submit support requests directly within the CRM using the new Internal Support form under
          Support in the sidebar. Supports 13 request types with conditional fields per type. Bug
          reports support file attachments. Submitting automatically creates a trackable ticket. See{' '}
          <a
            href="#internal-support"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Internal Support Requests
          </a>{' '}
          under Ticketing.
        </p>
      </>
    ),
  },
  {
    id: 'jira-field-on-tickets',
    title: 'Jira Field on Tickets',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Tickets now include a searchable Jira field. Enter a Jira ticket number (e.g.,
          GRACKLE-123) to link CRM tickets to your project tracking system. The field is available
          on both the Create Ticket page and the ticket detail page.
        </p>
      </>
    ),
  },
  {
    id: 'ticket-file-attachments',
    title: 'Ticket File Attachments',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Attach files to tickets via the new Attachments tab on the ticket detail page. Bug reports
          submitted through the Internal Support form also support file attachments that are linked
          to the created ticket.
        </p>
      </>
    ),
  },
  {
    id: 'sales-rep-info-on-pdf-quotes',
    title: 'Sales Rep Info on PDF Quotes',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Generated PDF quotes now include the sales representative's name, email, and phone number
          in a dedicated section next to the billing address. Customers can see exactly who to
          contact about the quote.
        </p>
      </>
    ),
  },
  {
    id: 'ticket-detail-edit-mode',
    title: 'Ticket Detail Edit Mode',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Edit all ticket fields inline from the detail page — subject, description, account,
          contact, owner, category, and channel. The contact dropdown filters by selected account.
          See{' '}
          <a
            href="#editing-tickets"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Editing Tickets
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'ticket-comment-edit-and-delete',
    title: 'Ticket Comment Edit & Delete',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Comment authors and admins can now edit or delete ticket comments via a dropdown menu on
          each comment card. Edited comments show an &ldquo;(edited)&rdquo; indicator. See{' '}
          <a
            href="#ticket-comments"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Comments
          </a>{' '}
          under Ticketing.
        </p>
      </>
    ),
  },
  {
    id: 'create-contacts-from-ticket-form',
    title: 'Create Contacts from Ticket Form',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          A &ldquo;+&rdquo; button next to the Contact dropdown on the Create Ticket page lets you
          create a new contact without leaving the form. The account is pre-populated if already
          selected.
        </p>
      </>
    ),
  },
  {
    id: 'ticket-reassignment-emails',
    title: 'Ticket Reassignment Emails',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When a ticket is reassigned, the new owner receives a branded HTML email with ticket
          details and a direct link. Works for individual reassignments and bulk updates.
        </p>
      </>
    ),
  },
  {
    id: 'escalated-ticket-status',
    title: 'Escalated Ticket Status',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          A new &ldquo;Escalated&rdquo; status is available for tickets that need to be raised to a
          higher priority or team for further attention.
        </p>
      </>
    ),
  },
  {
    id: 'tasks-on-tickets',
    title: 'Tasks on Tickets',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Support users can now create and manage tasks directly from the ticket detail page. Use
          the Tasks tab to add follow-up items with due dates, priorities, and descriptions —
          without leaving the ticket. See{' '}
          <a
            href="#ticket-tasks"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Tasks on Tickets
          </a>{' '}
          under Ticketing.
        </p>
      </>
    ),
  },
  {
    id: 'task-due-date-reminders',
    title: 'Task Due Date Reminders',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Automatic daily notifications for tasks that are due tomorrow, due today, or overdue.
          Reminders appear in the notification bell and are also sent via email and Google Chat (if
          configured). No manual checking required.
        </p>
      </>
    ),
  },
  {
    id: 'related-to-links-on-tasks',
    title: 'Related-To Links on Tasks',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Tasks can now link to any entity — accounts, opportunities, tickets, and more — via the
          Related To field. This makes it easy to see which record a task is associated with from
          the tasks list.
        </p>
      </>
    ),
  },
  {
    id: 'billing-address-on-opportunities',
    title: 'Billing Address on Opportunities',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Opportunities now include billing address fields so you can track where invoices should be
          sent directly on the deal record.
        </p>
      </>
    ),
  },
  {
    id: 'auto-create-renewal-opportunities',
    title: 'Auto-Create Renewal Opportunities',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When a closed opportunity's contract end date passes, the system automatically creates a
          renewal opportunity so your team can start the re-engagement process. See{' '}
          <a
            href="#sw-renewals"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Renewal Opportunities
          </a>{' '}
          under Sales Workflows.
        </p>
      </>
    ),
  },
  {
    id: 'recently-searched-dropdown',
    title: 'Recently Searched Dropdown',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The global search bar now shows your recent searches in a dropdown when you click into it.
          Quickly re-run a previous search without retyping.
        </p>
      </>
    ),
  },
  {
    id: 'clickable-search-result-names',
    title: 'Clickable Search Result Names',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Names in search results now link directly to the record detail page, so you can navigate
          with a single click.
        </p>
      </>
    ),
  },
  {
    id: 'settings-page-tabs',
    title: 'Settings Page Tabs',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The Settings page now uses a tabbed layout for easier navigation. Each section has its own
          tab instead of a long scrolling list. Picklist Management and Renewal Templates are now
          accessible from Settings tabs instead of separate pages. See{' '}
          <a
            href="#settings"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Settings
          </a>{' '}
          under Administration.
        </p>
      </>
    ),
  },
  {
    id: 'automated-renewal-tasks',
    title: 'Automated Renewal Tasks',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When an opportunity closes to Closed Won with a contract end date, renewal reminder tasks
          are automatically created based on configurable templates. Default reminders at 90, 60,
          and 45 days before contract end. Admins configure templates in{' '}
          <a
            href="#renewal-templates"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Settings &rarr; Renewals
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'google-chat-notifications',
    title: 'Google Chat Notifications',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Send CRM event notifications to a Google Chat space via webhook. Get notified when leads
          are created, opportunities change stage, or deals close — without checking the CRM.
          Configure in{' '}
          <a
            href="#google-chat-settings"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Settings &rarr; Google Chat
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'email-compose-and-send-via-gmail-api',
    title: 'Email Compose & Send via Gmail API',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Compose and send emails directly from the CRM using the Gmail API. Click Compose on the
          Emails page or from any activity tab. Reply to emails with automatic threading. Currently
          in beta — limited to @grackledocs.com recipients; emails to other domains are saved as
          drafts. See{' '}
          <a
            href="#emails"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Emails
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'mailchimp-integration',
    title: 'Mailchimp Integration',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Connect your Mailchimp account via API key to sync CRM contacts to audiences for email
          marketing campaigns. Available to Admin, Super Admin, and Marketing users. Supports Quick
          Sync and Full Sync with real-time progress tracking. See{' '}
          <a
            href="#mailchimp-integration"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Mailchimp Integration
          </a>{' '}
          under Administration.
        </p>
      </>
    ),
  },
  {
    id: 'real-time-notifications',
    title: 'Real-Time Notifications',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Instant notifications for assignments, closed won deals, SLA breaches, stage changes,
          comments, and overdue tasks. The bell icon in the header shows an unread count — click any
          notification to jump to the related record. See{' '}
          <a
            href="#notifications-settings"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Notifications
          </a>{' '}
          under Administration.
        </p>
      </>
    ),
  },
  {
    id: 'quote-pdf-generation',
    title: 'Quote PDF Generation',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Generate professional PDF proposals from any quote. Click View PDF to open in a new
          browser tab or Download PDF to save to your device. PDFs include company information,
          billing address, line items, discounts, and totals.
        </p>
      </>
    ),
  },
  {
    id: 'dark-mode',
    title: 'Dark Mode',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Toggle between light and dark mode using the sun/moon icon in the header. Your preference
          persists across sessions. All pages support both themes with proper contrast ratios for
          accessibility.
        </p>
      </>
    ),
  },
  {
    id: 'voip-call-logging',
    title: 'VoIP Call Logging',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Log every phone call directly in the CRM. Click any phone number to dial via your
          softphone (e.g., Zoiper) and open a call logging dialog with a live timer. Log incoming
          calls from the phone icon in the header — search by caller number to match contacts,
          leads, or accounts. Calls appear in the{' '}
          <a
            href="#call-logging"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Activity Timeline
          </a>{' '}
          alongside tasks, events, and emails. A new "Calls Today" card on the Dashboard tracks
          daily and weekly call volume.
        </p>
      </>
    ),
  },
  {
    id: 'predictive-analytics-suite',
    title: 'Predictive Analytics Suite',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Four new predictive features to help reps prioritize work and close deals faster:{' '}
          <a
            href="#stage-velocity"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Stage Velocity Tracker
          </a>{' '}
          shows how long a deal has been in its current stage vs. the average for won deals.{' '}
          <a
            href="#win-probability"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Win Probability
          </a>{' '}
          predicts whether a deal will close (5-95%) based on stage, velocity, close date slippage,
          and momentum.{' '}
          <a
            href="#account-health"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Account Health Score
          </a>{' '}
          rates each account (0-100) on engagement, revenue, renewal risk, and relationship breadth.{' '}
          <a
            href="#next-best-action"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Next Best Action
          </a>{' '}
          suggests a concrete step when a score is low.
        </p>
      </>
    ),
  },
  {
    id: 'lead-scoring',
    title: 'Lead Scoring',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Every active lead now shows a predictive score (0-100) combining engagement recency,
          profile completeness, lead source quality, and status timeliness. Color-coded badges in
          the Leads list help you prioritize which leads to work first, and a full breakdown card on
          the detail page shows exactly where to focus. See{' '}
          <a
            href="#lead-scoring"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Lead Scoring
          </a>{' '}
          under Analytics for the full scoring details.
        </p>
      </>
    ),
  },
  {
    id: 'deal-health-score',
    title: 'Deal Health Score',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Every open opportunity now shows a predictive health score (0-100) combining four signals:
          recent activity, sales process milestones, close date health, and stage probability.
          Color-coded badges appear in the Opportunities list, and a full breakdown card is
          available on the detail page. Use it to spot at-risk deals before they stall. See{' '}
          <a
            href="#deal-health"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Deal Health Score
          </a>{' '}
          under Analytics for the full scoring details.
        </p>
      </>
    ),
  },
  {
    id: 'new-user-types-marketing-orders-and-quotes',
    title: 'New User Types: Marketing, Orders & Quotes',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Three new specialized user roles with granular access control. Marketing users focus on
          leads and outreach, Orders users handle fulfillment, and Quotes users manage proposals.
          Each type sees only the CRM sections relevant to their job function. See{' '}
          <a
            href="#users"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            User Management
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'sales-user-display',
    title: 'Sales User Display',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The standard "User" role now displays as "Sales" throughout the interface for clarity. The
          two terms are interchangeable — the underlying permissions are unchanged.
        </p>
      </>
    ),
  },
  {
    id: 'closed-won-admin-restriction',
    title: 'Closed Won Admin Restriction',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Only Admin and Super Admin users can move opportunities to Closed Won or edit them after
          closing. An attachment is required before closing. This protects revenue data integrity.
        </p>
      </>
    ),
  },
  {
    id: 'auto-create-order-on-closed-won',
    title: 'Auto-Create Order on Closed Won',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When an opportunity is marked Closed Won, an order is automatically created and linked. No
          need to manually create orders — the transition from deal to fulfillment is seamless.
        </p>
      </>
    ),
  },
  {
    id: 'live-quotes-synced-quotes',
    title: 'Live Quotes (Synced Quotes)',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Create a synced quote on an opportunity. Changes to line items on the quote automatically
          update the opportunity amount, keeping your pipeline forecast accurate without manual
          updates.
        </p>
      </>
    ),
  },
  {
    id: 'order-status-stepper',
    title: 'Order Status Stepper',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Order detail pages now feature a visual status progression bar showing the current stage.
          Track orders Draft → Pending → Approved → Processed (or Denied) at a glance.
        </p>
      </>
    ),
  },
  {
    id: 'resizable-and-sortable-columns-on-orders',
    title: 'Resizable & Sortable Columns on Orders',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          The Orders list page now supports resizable and sortable columns, matching the pattern on
          other list pages. Drag column edges to resize and click headers to sort.
        </p>
      </>
    ),
  },
  {
    id: 'gmail-internal-email-filtering',
    title: 'Gmail Internal Email Filtering',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Gmail sync now automatically skips internal emails between grackledocs.com team members.
          Only emails involving external contacts or leads are synced, keeping your CRM focused on
          customer communications.
        </p>
      </>
    ),
  },
  {
    id: 'google-maps-address-autocomplete',
    title: 'Google Maps Address Autocomplete',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Start typing an address and select from Google Maps suggestions. The city, state, postal
          code, and country fields fill in automatically — saving time and reducing data entry
          errors. Available when creating or editing Accounts, Leads, Contacts, and Quotes.
        </p>
      </>
    ),
  },
  {
    id: 'default-owner-for-new-records',
    title: 'Default Owner for New Records',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When creating Opportunities or Leads, the Owner field now defaults to you. No need to
          select yourself every time.
        </p>
      </>
    ),
  },
  {
    id: 'enhanced-opportunity-creation',
    title: 'Enhanced Opportunity Creation',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          New Salesforce parity fields including Currency, Sale Type, Opportunity Category, Contract
          Details, and more. Plus, the Amount field now auto-updates when you add products to an
          opportunity.
        </p>
      </>
    ),
  },
  {
    id: 'enhanced-analytics-dashboard',
    title: 'Enhanced Analytics Dashboard',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          New Days to Close chart shows your average sales cycle length. SVG pipeline funnel
          visualization. Date range filtering with presets (30 Days, Quarter, YTD, 12 Months). See{' '}
          <a
            href="#analytics"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Analytics
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'quick-task-and-event-creation',
    title: 'Quick Task & Event Creation',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Create tasks and events directly from the Activity tab on Opportunity and Lead detail
          pages. The owner is pre-selected and the related record is linked automatically.
        </p>
      </>
    ),
  },
  {
    id: 'duplicate-detection-and-record-merging',
    title: 'Duplicate Detection & Record Merging',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Real-time duplicate warnings when creating accounts, contacts, and leads. Plus a merge
          tool that lets you combine two records into one clean master record, with all related data
          preserved. See{' '}
          <a
            href="#duplicate-detection"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Duplicate Detection
          </a>{' '}
          and{' '}
          <a
            href="#record-merging"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Record Merging
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'notes-on-lead-pages',
    title: 'Notes on Lead Pages',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Create notes directly from a lead's Notes tab to capture important context from calls and
          research. Notes carry over when you convert the lead.
        </p>
      </>
    ),
  },
  {
    id: 'contacts-from-account-pages',
    title: 'Contacts from Account Pages',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Add new contacts directly from an account's Contacts tab. The account and owner are
          pre-populated automatically, saving you time and reducing errors.
        </p>
      </>
    ),
  },
  {
    id: 'task-sorting-filtering-and-full-editing',
    title: 'Task Sorting, Filtering & Full Editing',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Sort tasks by date, priority, or status. Filter by date range. Edit tasks fully from the
          activity timeline on any record page. All dropdowns are now sorted alphabetically for
          easier selection.
        </p>
      </>
    ),
  },
  {
    id: 'auto-refresh-activity-tab',
    title: 'Auto-Refresh Activity Tab',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          When you create, edit, or complete a task, the Activity tab refreshes automatically — no
          need to reload the page to see your changes.
        </p>
      </>
    ),
  },
  {
    id: 'support-ticketing-system',
    title: 'Support Ticketing System',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Full ticketing system with SLA tracking, priority levels, comment threads with call logs,
          and a dedicated Support user role. See{' '}
          <a
            href="#ticketing"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Ticketing
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'google-meet-scheduling',
    title: 'Google Meet Scheduling',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Schedule Google Meet meetings directly from Account, Contact, and Opportunity detail
          pages. Meeting links are generated automatically.
        </p>
      </>
    ),
  },
  {
    id: 'custom-report-builder',
    title: 'Custom Report Builder',
    body: (
      <>
        <p className="text-sm text-muted-foreground">
          Build your own reports with flexible data sources, field selection, filters, and chart
          types. Live preview updates as you build. See{' '}
          <a
            href="#report-builder"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Custom Report Builder
          </a>
          .
        </p>
      </>
    ),
  },
]
